import type {
  MarketComparableDraft,
  MarketFunnelSnapshot,
  MarketListing,
  MarketProperty,
  MarketSubjectSnapshot,
  MarketTransaction,
  SimilarityScoreBreakdown
} from "../domain/types.ts";
import { calculateSimilarityScore } from "../calculations/similarity.ts";
import { buildMarketFunnelSnapshot, MARKET_FUNNEL_AREA_SIMILARITY_MINIMUM } from "../calculations/marketFunnel.ts";
import { normalizeLocationText } from "../location/locationSearch.ts";
import type { PublicMarketDiscoveryReport, PublicMarketSourceStatus } from "./publicMarketTypes.ts";
import { normalizeRoomCount } from "./roomNormalization.ts";
import {
  canonicalPropertyType,
  canonicalCondition,
  effectiveApartmentArea,
  isActiveAskingListing,
  isPlausibleApartmentArea,
  isPlausibleMarketPrice,
  isPlausiblePricePerM2,
  municipalityId,
  municipalityName,
  normalizeSlovakLocationName,
  sameMunicipality
} from "../domain/propertyMatching.ts";
import {
  MARKET_COMPARABLE_MAX,
  MARKET_QUALIFICATION_POLICY,
  marketLowDataWarning,
  qualifyMarketComparable
} from "../domain/selectionPolicy.ts";

export type MarketCandidate = MarketComparableDraft & {
  providerId: string;
  providerPropertyId?: string;
  transactionType?: MarketSubjectSnapshot["transactionType"];
};

export type MarketCandidateOptions = {
  areaTolerance?: number;
  fallbackAreaTolerance?: number;
  freshnessDays?: number;
  fallbackFreshnessDays?: number;
  limit?: number;
  discoveryScope?: "city-property-rooms";
};

export interface MarketDataProvider {
  readonly id: string;
  readonly available: boolean;
  findCandidates(subject: MarketSubjectSnapshot, options?: MarketCandidateOptions): Promise<MarketCandidate[]>;
  getListing(id: string): Promise<MarketListing | undefined>;
  getHistoricalListings(propertyId: string): Promise<MarketListing[]>;
  getRealizedTransactions(propertyId: string): Promise<MarketTransaction[]>;
  getProperty?(id: string): Promise<MarketProperty | undefined>;
  getDiscoveryReport?(): PublicMarketDiscoveryReport | undefined;
}

export type CandidatePipelineResult = {
  status: "ready" | "provider-unavailable";
  providerId: string;
  analyzedCount: number;
  broadPoolCount: number;
  selectedCandidateCount: number;
  prePoolRejectedCount: number;
  rankedOutCandidateCount: number;
  excludedCandidateCount: number;
  ineligibleCandidateCount: number;
  invalidCandidateCount: number;
  duplicateCandidateCount: number;
  eligibleCandidateCount: number;
  qualificationPolicy: typeof MARKET_QUALIFICATION_POLICY;
  subjectAreaBasis: ReturnType<typeof effectiveApartmentArea>;
  marketFunnel?: MarketFunnelSnapshot;
  lowDataWarning?: string;
  rejectionReasonCounts: Record<string, number>;
  rejectionPrimaryReasonCounts: Record<string, number>;
  rejectionSamplesByReason: Record<string, MarketCandidateAuditSample[]>;
  qualificationAudit: MarketCandidateQualificationAudit[];
  candidates: MarketCandidate[];
  sameConditionMarket: MarketCandidate[];
  otherConditionAlternatives: MarketCandidate[];
  marketCompetitors: MarketCandidate[];
  fallbackSelections: MarketCandidateFallbackSelection[];
  rejectedCandidates: MarketCandidateRejection[];
  sourceStatuses: PublicMarketSourceStatus[];
  rawListingCount: number;
  normalizedListingCount: number;
  preliminaryCandidateCount: number;
  detailListingCount: number;
  requestCount: number;
  uniqueListingCount: number;
  message: string;
};

export const MARKET_COMPETITION_MAX = 6;

export const marketCompetitionScore = (breakdown: SimilarityScoreBreakdown) => {
  const area = breakdown.components.area.available ? (breakdown.components.area.score ?? 0) * 45 : 0;
  const location = breakdown.components.location.available ? (breakdown.components.location.score ?? 0) * 25 : 0;
  const knownCondition = breakdown.components.technicalCondition.available ? 15 : 0;
  const completeness = breakdown.dataConfidence.score * 0.15;
  return Math.round((area + location + knownCondition + completeness) * 10) / 10;
};

const qualifiesAsMarketCompetition = (breakdown: SimilarityScoreBreakdown) =>
  breakdown.components.area.available
  && (breakdown.components.area.score ?? 0) >= 0.5
  && breakdown.dataConfidence.score >= 20
  && marketCompetitionScore(breakdown) >= 55;

export type MarketCandidateFallbackSelection = {
  candidate: MarketCandidate;
  reasons: string[];
};

export type MarketCandidateRejection = {
  candidate: MarketCandidate;
  similarityScore: number;
  reasons: string[];
  category: "ineligible" | "invalid" | "duplicate" | "ranked_out";
};

export type MarketCandidateAuditSample = {
  id: string;
  title: string;
  city: string;
  locality: string;
  condition: string;
  propertyType: string;
  rooms: string;
  area?: number;
  areaBasis: ReturnType<typeof effectiveApartmentArea>["basis"];
  askingPrice?: number;
  listingStatus: string;
  sourceUrl: string;
};

export type MarketCandidateQualificationAudit = MarketCandidateAuditSample & {
  similarityScore: number;
  dataConfidence: number;
  selectionScore: number;
  areaScore?: number;
  confirmedDimensions: number;
  qualified: boolean;
  qualificationReasons: string[];
  destination: "primary" | "same_condition" | "other_condition" | "excluded";
  destinationReason: string;
};

const same = (first?: string, second?: string) =>
  Boolean(first && second && normalizeLocationText(first) === normalizeLocationText(second));

const sameRoomCount = (first?: string, second?: string) => {
  const firstCount = normalizeRoomCount(first);
  const secondCount = normalizeRoomCount(second);
  if (firstCount !== undefined && secondCount !== undefined) return firstCount === secondCount;
  return same(first, second);
};

const ageInDays = (capturedAt: string, cutoff: string) => {
  const captured = Date.parse(capturedAt);
  const sourceCutoff = Date.parse(cutoff);
  if (!Number.isFinite(captured) || !Number.isFinite(sourceCutoff)) return undefined;
  return Math.max(0, (sourceCutoff - captured) / 86_400_000);
};

const broadPoolRejections = (subject: MarketSubjectSnapshot, candidate: MarketCandidate) => {
  const reasons: string[] = [];
  if (!sameMunicipality(subject, candidate)) reasons.push("iná obec");
  if (canonicalPropertyType(subject.propertyType) !== canonicalPropertyType(candidate.propertyType)) reasons.push("iný typ nehnuteľnosti");
  if (!sameRoomCount(subject.rooms, candidate.rooms)) reasons.push("iný počet izieb");
  return reasons;
};

const qualityRejections = (
  candidate: MarketCandidate,
  sourceCutoffAt: string
) => {
  const reasons: string[] = [];
  const candidateArea = effectiveApartmentArea(candidate).value;
  if (!isPlausibleApartmentArea(candidateArea)) reasons.push("chýba alebo je neplatná výmera");
  const ageDays = ageInDays(candidate.capturedAt, sourceCutoffAt);
  if (ageDays === undefined) reasons.push("neplatný dátum ponuky");
  const price = candidate.askingPrice;
  if (!isPlausibleMarketPrice(price) || !isPlausiblePricePerM2(price, candidateArea)) reasons.push("chýba alebo je neplatná cena");
  if (!isActiveAskingListing(candidate.listingStatus)) reasons.push("ponuka nie je aktívna");
  return reasons;
};

const canonicalCandidateUrl = (value: string) => {
  try {
    const url = new URL(value);
    url.hash = "";
    url.search = "";
    return `${url.hostname.replace(/^www\./i, "").toLocaleLowerCase("sk")}${url.pathname.replace(/\/$/, "")}`;
  } catch {
    return "";
  }
};

export const marketCandidateDedupeKey = (candidate: MarketCandidate) => candidate.providerPropertyId
  ? `${candidate.providerId}|id:${candidate.providerPropertyId}`
  : canonicalCandidateUrl(candidate.sourceUrl)
    ? `${candidate.providerId}|url:${canonicalCandidateUrl(candidate.sourceUrl)}`
    : [
  municipalityId(candidate) || normalizeSlovakLocationName(municipalityName(candidate)),
  candidate.streetId || normalizeSlovakLocationName(candidate.location?.street || candidate.street),
  normalizeRoomCount(candidate.rooms) ?? normalizeLocationText(candidate.rooms),
  Math.round(effectiveApartmentArea(candidate).value ?? 0),
  candidate.floor ?? "",
  Math.round(candidate.realizedPrice ?? candidate.askingPrice ?? 0),
  normalizeLocationText(String(candidate.sourceSnapshot?.title ?? ""))
    ].join("|");

const candidateAuditSample = (candidate: MarketCandidate): MarketCandidateAuditSample => {
  const area = effectiveApartmentArea(candidate);
  return {
    id: candidate.providerPropertyId ?? candidate.sourceUrl,
    title: String(candidate.sourceSnapshot?.title ?? "") || candidate.street || candidate.locality || `${candidate.rooms} ${candidate.propertyType}`,
    city: municipalityName(candidate),
    locality: candidate.street || candidate.locality,
    condition: candidate.condition,
    propertyType: candidate.propertyType,
    rooms: candidate.rooms,
    area: area.value,
    areaBasis: area.basis,
    askingPrice: candidate.askingPrice,
    listingStatus: candidate.listingStatus,
    sourceUrl: candidate.sourceUrl
  };
};

const marketCandidateRankingKey = (candidate: MarketCandidate) => [
  candidate.providerId,
  candidate.providerPropertyId ?? "",
  candidate.sourceUrl,
  normalizeLocationText(String(candidate.sourceSnapshot?.title ?? ""))
].join("|");

export const runCandidatePipeline = async (
  provider: MarketDataProvider,
  subject: MarketSubjectSnapshot,
  sourceCutoffAt: string,
  options: MarketCandidateOptions = {}
): Promise<CandidatePipelineResult> => {
  if (!provider.available) {
    return {
      status: "provider-unavailable",
      providerId: provider.id,
      analyzedCount: 0,
      broadPoolCount: 0,
      selectedCandidateCount: 0,
      prePoolRejectedCount: 0,
      rankedOutCandidateCount: 0,
      excludedCandidateCount: 0,
      ineligibleCandidateCount: 0,
      invalidCandidateCount: 0,
      duplicateCandidateCount: 0,
      eligibleCandidateCount: 0,
      qualificationPolicy: MARKET_QUALIFICATION_POLICY,
      subjectAreaBasis: effectiveApartmentArea(subject),
      rejectionReasonCounts: {},
      rejectionPrimaryReasonCounts: {},
      rejectionSamplesByReason: {},
      qualificationAudit: [],
      candidates: [],
      sameConditionMarket: [],
      otherConditionAlternatives: [],
      marketCompetitors: [],
      fallbackSelections: [],
      rejectedCandidates: [],
      sourceStatuses: [],
      rawListingCount: 0,
      normalizedListingCount: 0,
      preliminaryCandidateCount: 0,
      detailListingCount: 0,
      requestCount: 0,
      uniqueListingCount: 0,
      message: "Automatické porovnania nie sú dostupné – chýba live market provider."
    };
  }

  const resolved = {
    areaTolerance: options.areaTolerance ?? 0.4,
    fallbackAreaTolerance: options.fallbackAreaTolerance ?? 0.65,
    freshnessDays: options.freshnessDays ?? 365,
    fallbackFreshnessDays: options.fallbackFreshnessDays ?? 730,
    limit: Math.min(options.limit ?? MARKET_COMPARABLE_MAX, MARKET_COMPARABLE_MAX),
    discoveryScope: options.discoveryScope ?? "city-property-rooms" as const
  };
  const source = await provider.findCandidates(subject, resolved);
  const discoveryReport = provider.getDiscoveryReport?.();
  const unique = new Map<string, MarketCandidate>();
  const rejectedCandidates: MarketCandidateRejection[] = [];

  source.forEach((candidate) => {
    const score = calculateSimilarityScore(subject, candidate, sourceCutoffAt).finalScore;
    const reasons = broadPoolRejections(subject, candidate);
    if (reasons.length > 0) {
      rejectedCandidates.push({ candidate, similarityScore: score, reasons, category: "ineligible" });
      return;
    }
    const key = marketCandidateDedupeKey(candidate);
    if (unique.has(key)) {
      rejectedCandidates.push({ candidate, similarityScore: score, reasons: ["duplicita"], category: "duplicate" });
      return;
    }
    unique.set(key, candidate);
  });
  const prePoolRejectedCount = rejectedCandidates.length;

  const broadPool = [...unique.values()];
  const ranked = broadPool
    .map((candidate) => {
      const breakdown = calculateSimilarityScore(subject, candidate, sourceCutoffAt);
      return {
        candidate,
        breakdown,
        score: breakdown.finalScore,
        dataConfidence: breakdown.dataConfidence.score,
        // Confidence is a tie-break quality signal, not part of the displayed similarity.
        selectionScore: breakdown.selectionScore,
        qualityRejections: qualityRejections(candidate, sourceCutoffAt),
        qualification: qualifyMarketComparable(breakdown),
        // Asking price is intentionally absent: it must not decide selection, even as a tie-break.
        key: marketCandidateRankingKey(candidate)
      };
    })
    .sort((first, second) =>
      second.selectionScore - first.selectionScore
      || second.score - first.score
      || second.dataConfidence - first.dataConfidence
      || Date.parse(second.candidate.capturedAt) - Date.parse(first.candidate.capturedAt)
      || first.key.localeCompare(second.key, "sk")
    );

  const technicallyInvalidReasons = new Set([
    "chýba alebo je neplatná výmera",
    "neplatný dátum ponuky",
    "chýba alebo je neplatná cena"
  ]);
  const blocked = ranked.filter((item) => item.qualityRejections.length > 0);
  blocked.forEach((item) => rejectedCandidates.push({
    candidate: item.candidate,
    similarityScore: item.score,
    reasons: item.qualityRejections,
    category: item.qualityRejections.some((reason) => technicallyInvalidReasons.has(reason)) ? "invalid" : "ineligible"
  }));

  const valid = ranked.filter((item) => item.qualityRejections.length === 0);
  const subjectCondition = canonicalCondition(subject.technical?.condition ?? subject.condition);
  const hasComparableArea = (item: typeof valid[number]) => item.breakdown.components.area.available
    && (item.breakdown.components.area.score ?? 0) >= MARKET_FUNNEL_AREA_SIMILARITY_MINIMUM;
  const hasSameKnownCondition = (item: typeof valid[number]) => subjectCondition !== "unknown"
    && canonicalCondition(item.candidate.technical?.condition ?? item.candidate.condition) === subjectCondition;
  const hasOtherKnownCondition = (item: typeof valid[number]) => {
    const condition = canonicalCondition(item.candidate.technical?.condition ?? item.candidate.condition);
    return subjectCondition !== "unknown" && condition !== "unknown" && condition !== subjectCondition;
  };

  // Buyer-market eligibility is deliberately separate from the unchanged CMA qualification policy.
  const sameConditionPool = valid.filter((item) => hasComparableArea(item) && hasSameKnownCondition(item));
  const qualified = sameConditionPool.filter((item) => item.qualification.qualified);
  const selected = qualified.slice(0, resolved.limit);
  const selectedKeys = new Set(selected.map((item) => item.key));
  const sameConditionMarketRanked = sameConditionPool.filter((item) => !selectedKeys.has(item.key));
  const qualificationFailureReasons = (item: typeof valid[number]) => item.qualification.qualified
    ? [`nad maximálnym limitom ${resolved.limit} porovnaní`]
    : item.qualification.reasons;
  const sameConditionMarket = sameConditionMarketRanked.map((item) => ({
    ...item.candidate,
    marketRole: "competition" as const,
    marketContextGroup: "same_condition" as const,
    primaryQualificationReasons: qualificationFailureReasons(item)
  }));
  const otherConditionRanked = valid
    .filter((item) => hasComparableArea(item) && hasOtherKnownCondition(item))
    .filter((item) => qualifiesAsMarketCompetition(item.breakdown))
    .sort((first, second) =>
      marketCompetitionScore(second.breakdown) - marketCompetitionScore(first.breakdown)
      || second.dataConfidence - first.dataConfidence
      || second.score - first.score
      || first.key.localeCompare(second.key, "sk")
    )
    .slice(0, MARKET_COMPETITION_MAX);
  const otherConditionAlternatives = otherConditionRanked.map(({ candidate }) => ({
    ...candidate,
    marketRole: "competition" as const,
    marketContextGroup: "other_condition" as const,
    primaryQualificationReasons: ["materiálne odlišný stav nehnuteľnosti"]
  }));
  const sameConditionMarketKeys = new Set(sameConditionMarketRanked.map((item) => item.key));
  const otherConditionKeys = new Set(otherConditionRanked.map((item) => item.key));
  valid.filter((item) => !selectedKeys.has(item.key)).forEach((item) => {
    const reasons = sameConditionMarketKeys.has(item.key)
      ? qualificationFailureReasons(item)
      : hasComparableArea(item) && hasOtherKnownCondition(item)
        ? ["materiálne odlišný stav nehnuteľnosti"]
        : !hasComparableArea(item)
          ? ["výmera mimo porovnateľného pásma"]
          : ["stav nehnuteľnosti nie je potvrdený"];
    rejectedCandidates.push({
      candidate: item.candidate,
      similarityScore: item.score,
      reasons,
      category: item.qualification.qualified && sameConditionMarketKeys.has(item.key) ? "ranked_out" : "ineligible"
    });
  });
  const candidates = selected.map(({ candidate }) => candidate);
  const marketCompetitors = [...sameConditionMarket, ...otherConditionAlternatives];
  const qualificationAudit = valid.map((item): MarketCandidateQualificationAudit => {
    const destination = selectedKeys.has(item.key)
      ? "primary" as const
      : sameConditionMarketKeys.has(item.key)
        ? "same_condition" as const
        : otherConditionKeys.has(item.key)
          ? "other_condition" as const
          : "excluded" as const;
    const destinationReason = destination === "primary"
      ? "spĺňa Primary CMA kvalifikáciu"
      : destination === "same_condition"
        ? qualificationFailureReasons(item).join(" · ")
        : destination === "other_condition"
          ? "materiálne odlišný stav nehnuteľnosti"
          : !hasComparableArea(item)
            ? "výmera mimo porovnateľného pásma"
            : "stav nehnuteľnosti nie je potvrdený";
    return {
      ...candidateAuditSample(item.candidate),
      similarityScore: item.score,
      dataConfidence: item.dataConfidence,
      selectionScore: item.selectionScore,
      areaScore: item.breakdown.components.area.score,
      confirmedDimensions: item.qualification.confirmedDimensions,
      qualified: item.qualification.qualified,
      qualificationReasons: item.qualification.reasons,
      destination,
      destinationReason
    };
  });
  const fallbackSelections: MarketCandidateFallbackSelection[] = [];
  const rankedOutCandidateCount = rejectedCandidates.filter((item) => item.category === "ranked_out").length;
  const ineligibleCandidateCount = rejectedCandidates.filter((item) => item.category === "ineligible").length;
  const invalidCandidateCount = rejectedCandidates.filter((item) => item.category === "invalid").length;
  const duplicateCandidateCount = rejectedCandidates.filter((item) => item.category === "duplicate").length;
  const eligibleCandidateCount = qualified.length;
  const rejectionReasonCounts = rejectedCandidates.reduce<Record<string, number>>((counts, item) => {
    item.reasons.forEach((reason) => { counts[reason] = (counts[reason] ?? 0) + 1; });
    return counts;
  }, {});
  const rejectionPrimaryReasonCounts = rejectedCandidates.reduce<Record<string, number>>((counts, item) => {
    const reason = item.reasons[0] ?? "iný dôvod";
    counts[reason] = (counts[reason] ?? 0) + 1;
    return counts;
  }, {});
  const rejectionSamplesByReason = rejectedCandidates.reduce<Record<string, MarketCandidateAuditSample[]>>((samples, item) => {
    item.reasons.forEach((reason) => {
      if ((samples[reason]?.length ?? 0) >= 3) return;
      samples[reason] = [...(samples[reason] ?? []), candidateAuditSample(item.candidate)];
    });
    return samples;
  }, {});
  const marketFunnel = buildMarketFunnelSnapshot({
    providerId: provider.id,
    subject,
    sourceCutoffAt,
    hardEligibleCandidates: broadPool,
    validMarketCandidates: valid.map(({ candidate }) => candidate),
    selectedPrimary: candidates,
    marketCompetitors,
    sameConditionMarket,
    otherConditionAlternatives,
    rejectedCandidates,
    upstreamDuplicateCount: Math.max(
      0,
      (discoveryReport?.normalizedCount ?? source.length) - (discoveryReport?.uniqueCount ?? source.length)
    ) + duplicateCandidateCount
  });

  return {
    status: "ready",
    providerId: provider.id,
    analyzedCount: source.length,
    broadPoolCount: broadPool.length,
    selectedCandidateCount: candidates.length,
    prePoolRejectedCount,
    rankedOutCandidateCount,
    excludedCandidateCount: rejectedCandidates.length,
    ineligibleCandidateCount,
    invalidCandidateCount,
    duplicateCandidateCount,
    eligibleCandidateCount,
    qualificationPolicy: MARKET_QUALIFICATION_POLICY,
    subjectAreaBasis: effectiveApartmentArea(subject),
    marketFunnel,
    lowDataWarning: marketLowDataWarning(candidates.length),
    rejectionReasonCounts,
    rejectionPrimaryReasonCounts,
    rejectionSamplesByReason,
    qualificationAudit,
    candidates,
    sameConditionMarket,
    otherConditionAlternatives,
    marketCompetitors,
    fallbackSelections,
    rejectedCandidates,
    sourceStatuses: discoveryReport?.sourceStatuses ?? [],
    rawListingCount: discoveryReport?.rawCount ?? source.length,
    normalizedListingCount: discoveryReport?.normalizedCount ?? source.length,
    preliminaryCandidateCount: discoveryReport?.preliminaryCount ?? source.length,
    detailListingCount: discoveryReport?.detailCount ?? 0,
    requestCount: discoveryReport?.requestCount ?? 0,
    uniqueListingCount: discoveryReport?.uniqueCount ?? source.length,
    message: candidates.length > 0
      ? `Vybraných ${candidates.length} kvalitných porovnaní z ${broadPool.length} kandidátov.`
      : "Pre zadanú obec, typ a počet izieb sa nenašli porovnania spĺňajúce minimálnu kvalitu."
  };
};

export const unavailableMarketDataProvider: MarketDataProvider = {
  id: "unavailable",
  available: false,
  async findCandidates() { return []; },
  async getListing() { return undefined; },
  async getHistoricalListings() { return []; },
  async getRealizedTransactions() { return []; }
};
